/**
 * Secure Logger Utility
 * Prevents sensitive information disclosure in production console logs
 */

interface LogData {
  [key: string]: any;
}

class SecureLogger {
  private readonly isDevelopment = process.env.NODE_ENV === 'development';
  private readonly sensitiveFields = [
    'token', 'password', 'secret', 'key', 'auth', 'authorization',
    'jwt', 'bearer', 'credential', 'api_key', 'access_token'
  ];

  /**
   * Debug logging - only in development
   */
  debug(message: string, data?: LogData): void {
    if (this.isDevelopment) {
      console.log(`[DEBUG] ${message}`, this.sanitizeData(data));
    }
  }

  /**
   * Info logging - only in development
   */
  info(message: string, data?: LogData): void {
    if (this.isDevelopment) {
      console.info(`[INFO] ${message}`, this.sanitizeData(data));
    }
  }

  /**
   * Warning logging - always shown but sanitized
   */
  warn(message: string, data?: LogData): void {
    console.warn(`[WARN] ${message}`, this.sanitizeData(data));
  }

  /**
   * Error logging - always shown but sanitized
   */
  error(message: string, error?: any): void {
    console.error(`[ERROR] ${message}`, this.sanitizeError(error));
  }

  /**
   * API request logging - development only
   */
  apiRequest(method: string, url: string, data?: LogData): void {
    if (this.isDevelopment) {
      console.log(`[API] ${method.toUpperCase()} ${url}`, this.sanitizeData(data));
    }
  }

  /**
   * API response logging - development only
   */
  apiResponse(url: string, status: number, data?: LogData): void {
    if (this.isDevelopment) {
      console.log(`[API] Response ${status} from ${url}`, this.sanitizeData(data));
    }
  }

  /**
   * Authentication flow logging - development only
   */
  auth(message: string, data?: LogData): void {
    if (this.isDevelopment) {
      console.log(`[AUTH] ${message}`, this.sanitizeData(data));
    }
  }

  /**
   * Export/download logging - development only
   */
  export(message: string, data?: LogData): void {
    if (this.isDevelopment) {
      console.log(`[EXPORT] ${message}`, this.sanitizeData(data));
    }
  }

  /**
   * Remove sensitive data from log objects
   */
  private sanitizeData(data?: LogData): LogData | undefined {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const sanitized: LogData = {};

    for (const [key, value] of Object.entries(data)) {
      const keyLower = key.toLowerCase();
      
      // Check if key contains sensitive information
      const isSensitive = this.sensitiveFields.some(field => 
        keyLower.includes(field)
      );

      if (isSensitive) {
        if (typeof value === 'string' && value.length > 10) {
          // Show first few characters for debugging
          sanitized[key] = `${value.substring(0, 8)}...[REDACTED]`;
        } else {
          sanitized[key] = '[REDACTED]';
        }
      } else if (typeof value === 'object' && value !== null) {
        // Recursively sanitize nested objects
        sanitized[key] = this.sanitizeData(value);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Sanitize error objects for safe logging
   */
  private sanitizeError(error?: any): any {
    if (!error) return error;

    const sanitized: any = {
      message: error.message || 'Unknown error',
      name: error.name || 'Error'
    };

    // Include stack trace only in development
    if (this.isDevelopment && error.stack) {
      sanitized.stack = error.stack;
    }

    // Sanitize any additional properties
    if (error.response) {
      sanitized.response = {
        status: error.response.status,
        statusText: error.response.statusText,
        // Don't include response data as it might contain sensitive info
      };
    }

    return sanitized;
  }

  /**
   * Performance timing logging - development only
   */
  performance(operation: string, startTime: number): void {
    if (this.isDevelopment) {
      const duration = Date.now() - startTime;
      console.log(`[PERF] ${operation} completed in ${duration}ms`);
    }
  }

  /**
   * User action logging - development only
   */
  userAction(action: string, data?: LogData): void {
    if (this.isDevelopment) {
      console.log(`[USER] ${action}`, this.sanitizeData(data));
    }
  }
}

// Export singleton instance
export const logger = new SecureLogger();

// Export type for external use
export type { LogData };
